import React, { useState } from "react";
import CreateModal from "./CreateModal";
import StatusModal from "./StatusModal";
import {useService} from "../../../base/hooks/useService";
import UserResultsService from "../../../services/UserResults";
import ToasterService from "../../../services/ToastService";
import { TUBE_OPTIONS } from "../../../groups/app/Biomarkers/CreateEdit/constants.js";
import { ConfirmPopupWithReason } from "../ConfirmPopup";

export default function RetestOrderModal({ isOpen, onClose, result }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState("yellow");
  const [selectedBiomarkers, setSelectedBiomarkers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalState, setModalState] = useState("status"); // 'status' | 'create'
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const toastService = useService(ToasterService);
  const userResultsService = useService(UserResultsService);

  const resetModal = () => {
    setStep(1);
    setSelectedType("yellow");
    setSelectedBiomarkers([]);
    setIsSubmitting(false);
    setModalState("status");
    setShowCancelConfirm(false);
  };

  const handleClose = (status) => {
    resetModal();
    onClose(status);
  };

  const handleCancelRetestRequest = async (values) => {
    try {
      await userResultsService.cancelRetestRequest(
        result.id,
        result.retestRequest.id,
        { cancellationReason: values.cancellationReason }
      );
      toastService.success("Retest request cancelled successfully");
      setShowCancelConfirm(false);
      resetModal();
      onClose("cancelled");
    } catch (err) {
      toastService.error(err.message || "Failed to cancel retest request");
      setShowCancelConfirm(false);
    }
  };

  const handleSubmit = async () => {
    /**
     * @type {ToasterService}
     */
    const toastService = useService(ToasterService);
    setIsSubmitting(true);
    
    try {
      const userResultsService = new UserResultsService();
      const tubeType = TUBE_OPTIONS[selectedType.toUpperCase()];
      await userResultsService.createRetestOrder(result.id, {
        tube: tubeType,
        biomarkerIds: selectedBiomarkers.map(b => b.id)
      });
      toastService.success("Retest order created successfully");
      handleClose("pending");
    } catch (err) {
      toastService.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
    else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  }

  if (!result.retestRequest) {
    return (
      <CreateModal
        isOpen={isOpen}
        handleClose={handleClose}
        step={step}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedBiomarkers={selectedBiomarkers}
        setSelectedBiomarkers={setSelectedBiomarkers}
        isSubmitting={isSubmitting}
        handleBack={handleBack}
        handleNext={handleNext}
        result={result}
      />
    );
  } else {
    return (
      <>
        <StatusModal
          isOpen={isOpen && modalState === "status"}
          handleClose={handleClose}
          retestRequest={result.retestRequest}
          onCreateRetest={() => setModalState("create")}
          onCancelRequest={() => setShowCancelConfirm(true)}
        />
        {modalState === "create" && (
          <CreateModal
            isOpen={isOpen}
            handleClose={() => {
              setModalState("status");
              handleClose();
            }}
            step={step}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedBiomarkers={selectedBiomarkers}
            setSelectedBiomarkers={setSelectedBiomarkers}
            isSubmitting={isSubmitting}
            handleBack={handleBack}
            handleNext={handleNext}
            result={result}
          />
        )}
        <ConfirmPopupWithReason
          isOpen={showCancelConfirm}
          updateIsOpen={setShowCancelConfirm}
          onSubmit={handleCancelRetestRequest}
          skipOnClose
          title="Cancel Retest Request"
          description="Are you sure you want to cancel retest request?"
          submitBtnText="Confirm"
          labelText="Reason for cancellation:"
          payloadText="cancellationReason"
          validationText="Cancellation reason is required."
        />
      </>
    );
  }
}
